import React from "react"
import ReactMarkdown, { Components } from "react-markdown"
import remarkGfm from "remark-gfm"
import remarkDirective from "remark-directive"
import { visit } from "unist-util-visit"
import type { Node } from "unist"
import { Link } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLink } from "@fortawesome/free-solid-svg-icons"

import remarkCallouts from "features/Docs/utils/remarkCallouts"
import { createSlugger } from "features/Docs/utils/slugs"
import DocsCodeBlock from "features/Docs/components/DocsCodeBlock.component"
import DocsCallout from "features/Docs/components/DocsCallout.component"

interface DocsMarkdownProps {
  body: string
}

interface MdNode {
  type: string
  depth?: number
  value?: string
  children?: MdNode[]
  data?: {
    hProperties?: Record<string, unknown>
  }
}

/** Text content of an mdast node (concatenated leaf values). */
const mdText = (node: MdNode): string => {
  if (typeof node.value === "string") return node.value
  return (node.children ?? []).map(mdText).join("")
}

/**
 * remark plugin: assign GitHub-style ids to h2–h4. The slugger is created
 * fresh for every transform run (i.e. per page render), so ids are
 * deterministic and de-duplicated in document order — no state leaks across
 * re-renders or StrictMode double passes.
 */
const remarkHeadingIds = () => {
  return (tree: Node): void => {
    const slugger = createSlugger()
    visit(tree, "heading", (node) => {
      const heading = node as unknown as MdNode
      if (!heading.depth || heading.depth < 2 || heading.depth > 4) return
      const data = (heading.data ??= {})
      data.hProperties = {
        ...(data.hProperties ?? {}),
        id: slugger.slug(mdText(heading)),
      }
    })
  }
}

const headingRenderer = (Tag: "h2" | "h3" | "h4") => {
  const Heading = ({
    id,
    children,
  }: React.HTMLAttributes<HTMLHeadingElement>): React.ReactElement => (
    <Tag id={id} className="docs-heading">
      {children}
      {id && (
        <a
          className="docs-anchor"
          href={`#${id}`}
          aria-label="Link to this section"
        >
          <FontAwesomeIcon icon={faLink} />
        </a>
      )}
    </Tag>
  )
  Heading.displayName = `DocsHeading(${Tag})`
  return Heading
}

const components: Components = {
  h2: headingRenderer("h2"),
  h3: headingRenderer("h3"),
  h4: headingRenderer("h4"),
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children }) => {
    const text = String(children ?? "")
    const match = /language-(\w+)/.exec(className || "")
    const isBlock = Boolean(match) || text.includes("\n")
    if (isBlock) {
      return <DocsCodeBlock language={match?.[1]} code={text} />
    }
    return <code className="docs-inline-code">{children}</code>
  },
  table: ({ children }) => (
    <div className="docs-table-wrap">
      <table>{children}</table>
    </div>
  ),
  a: ({ href, children }) => {
    if (href && href.startsWith("/docs")) {
      return <Link to={href}>{children}</Link>
    }
    const external = href ? /^https?:/i.test(href) : false
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
      >
        {children}
      </a>
    )
  },
  div: (props) => {
    const record = props as Record<string, unknown>
    const callout = (record["data-callout"] ?? record["dataCallout"]) as
      | string
      | undefined
    if (callout) {
      const title = (record["data-title"] ?? record["dataTitle"]) as
        | string
        | undefined
      return (
        <DocsCallout kind={callout} title={title}>
          {props.children}
        </DocsCallout>
      )
    }
    return <div>{props.children}</div>
  },
}

/**
 * Renders a docs page body (headings start at `##`) with the DATA(Q) docs
 * pipeline: GFM tables, container-directive callouts, statically highlighted
 * code blocks, slugged headings with hover anchors, wrapped tables, and
 * internal links routed through react-router.
 */
const DocsMarkdown: React.FC<DocsMarkdownProps> = ({ body }) => (
  <div className="docs-markdown">
    <ReactMarkdown
      remarkPlugins={[
        remarkGfm,
        remarkDirective,
        remarkCallouts,
        remarkHeadingIds,
      ]}
      components={components}
    >
      {body}
    </ReactMarkdown>
  </div>
)

export default DocsMarkdown
